import {ChoiceKey} from 'models/configuration/ChoiceModel'
import {OptionKey, OptionModel} from 'models/configuration/OptionModel'
import {
	CommentsState,
	OptionsState,
	RegistrationModel
} from 'models/registration/RegistrationModel'
import {StepConfig, StepKey, StepModel} from 'models/registration/StepModel'
import {Memoize} from 'util/memoize'

export type ConfigureStepConfig = StepConfig

export type ConfigureStepOptions = Record<OptionKey, ChoiceKey | null>

export class ConfigureStepModel extends StepModel {
	constructor(
		registration: RegistrationModel,
		config: StepConfig,
		readonly isBreakouts: boolean
	) {
		super(registration, config)
	}

	@Memoize()
	get key(): StepKey {
		return this.isBreakouts ? 'breakouts' : 'configure'
	}

	@Memoize()
	get options() {
		return this.registration.options.filter(option => {
			if (option.config.backend_only) return false
			if (this.isBreakouts) return option.key.startsWith('breakout')
			return !option.key.startsWith('breakout')
		})
	}

	@Memoize()
	get isApplicable(): boolean {
		return this.options.length > 0
	}

	@Memoize()
	get state(): ConfigureStepOptions {
		return {
			...this.registration.options.reduce(
				(obj, option) => ({
					...obj,
					[option.key]: option.getChoiceFor(this.registration)?.key
				}),
				{}
			)
		}
	}

	@Memoize()
	get isReady(): boolean {
		return !this.options.find(option => !option.getChoiceFor(this.registration))
	}

	// ----- State changes ----- //
	setOptionsAndComments(
		options: Array<OptionModel>,
		selectedOptionsAndComments: Record<string, string>
	): RegistrationModel {
		const optionTags = options.reduce((tags, option) => {
			const selectedChoice = selectedOptionsAndComments[option.key]
			const choiceTags = option.choices.reduce(
				(acc, choice) =>
					choice.tag
						? choice.key === selectedChoice
							? {...acc, [choice.tag]: true}
							: {...acc, [choice.tag]: false}
						: acc,
				{}
			)
			return {...tags, ...choiceTags}
		}, {})

		const tags = {...this.registration.state.tags, ...optionTags}

		// Remove false choices
		for (const key in tags) {
			if (tags[key] === false) delete tags[key]
		}

		const selectedOptions: OptionsState = {}
		const comments: CommentsState = {}
		options.forEach(option => {
			if (selectedOptionsAndComments[option.key]) {
				const choice = option.choices.find(
					choice => choice.key === selectedOptionsAndComments[option.key]
				)
				if (choice) {
					selectedOptions[option.key] = choice.key
					if (choice.shouldIncludeComments) {
						comments[`${option.key}_comments`] =
							selectedOptionsAndComments[`${option.key}_comments`] || ''
					}
				}
			}
		})

		return this.registration.update({
			options: selectedOptionsAndComments,
			comments,
			tags
		})
	}
}
